import { LIST_SEARCH_ANALYTICS_VIEWS_NAME } from "@/lib/ai/tools/list-search-analytics-views/constants";
import type { ListSaViewsOutput } from "@/lib/ai/tools/list-search-analytics-views/server";
import {
  defineDatasetSpec,
  expectVisualizationObjectOutput,
} from "@/lib/ai/visualization/data/adapters/adapter-contract";
import {
  buildDataset,
  buildFields,
  toListBoolean,
  toListNumber,
  toListText,
  toListTextList,
} from "@/lib/ai/visualization/data/adapters/dataset-normalization";
import type {
  VizDataset,
  VizDatasetRow,
} from "@/lib/ai/visualization/data/schema";

const SA_VIEWS_DATASET_SPEC = defineDatasetSpec({
  id: `${LIST_SEARCH_ANALYTICS_VIEWS_NAME}-views`,
  profile: {
    datasetFamily: "list.searchAnalyticsViews",
    variant: "views",
    compareMode: "none",
    supportedViews: ["bar-comparison", "bar-ranking", "table"],
  },
  fields: [
    { key: "searchAnalyticsId", role: "dim", type: "string" },
    { key: "viewId", role: "dim", type: "string" },
    { key: "viewName", role: "dim", type: "string" },
    { key: "description", role: "dim", type: "string" },
    { key: "dateType", role: "dim", type: "string" },
    { key: "dateStart", role: "dim", type: "string" },
    { key: "dateEnd", role: "dim", type: "string" },
    { key: "rawDataGroupBy", role: "dim", type: "string" },
    { key: "isAnonymousQuery", role: "dim", type: "string" },
    { key: "countries", role: "dim", type: "string" },
    { key: "devices", role: "dim", type: "string" },
    { key: "searchTypes", role: "dim", type: "string" },
    { key: "queryFilterType", role: "dim", type: "string" },
    { key: "query", role: "dim", type: "string" },
    { key: "queryGroupCount", role: "metric", type: "number", unit: "count" },
    { key: "urlFilterType", role: "dim", type: "string" },
    { key: "url", role: "dim", type: "string" },
    { key: "urlGroupCount", role: "metric", type: "number", unit: "count" },
    { key: "rawKinds", role: "dim", type: "string" },
    { key: "rawKindsNot", role: "dim", type: "string" },
  ] as const,
  defaults: {
    category: "viewName",
    value: "queryGroupCount",
    y: ["queryGroupCount", "urlGroupCount"],
  },
  notes:
    "Comparison-ready dataset normalized from list_search_analytics_views output.",
});

function toSaViewRows(output: Partial<ListSaViewsOutput>): VizDatasetRow[] {
  const views = Array.isArray(output.views) ? output.views : [];

  return views.map((view) => ({
    searchAnalyticsId: toListText(output.searchAnalyticsId),
    viewId: toListText(view.id),
    viewName: toListText(view.name),
    description: toListText(view.description),
    dateType: toListText(view.dataDate?.type),
    dateStart: toListText(view.dataDate?.dateRange?.start),
    dateEnd: toListText(view.dataDate?.dateRange?.end),
    rawDataGroupBy: toListTextList(view.rawDataGroupBy),
    isAnonymousQuery: toListBoolean(view.isAnonymousQuery),
    countries: toListTextList(view.countries),
    devices: toListTextList(view.devices),
    searchTypes: toListTextList(view.searchTypes),
    queryFilterType: toListText(view.queryFilterType),
    query: toListText(view.query),
    queryGroupCount: toListNumber(
      Array.isArray(view.queryGroupIDs) ? view.queryGroupIDs.length : 0,
    ),
    urlFilterType: toListText(view.urlFilterType),
    url: toListText(view.url),
    urlGroupCount: toListNumber(
      Array.isArray(view.urlGroupIDs) ? view.urlGroupIDs.length : 0,
    ),
    rawKinds: toListTextList(view.rawKinds),
    rawKindsNot: toListTextList(view.rawKindsNot),
  }));
}

/**
 * list_search_analytics_views 出力を views comparison 向け dataset へ正規化する。
 *
 * @remarks
 * saved view の棚卸しは一覧だけでなく query/url group count の比較でも使うため、
 * table 専用に閉じず `bar-comparison` / `bar-ranking` へも接続できる profile にする。
 * tool 契約外の非 object payload は空 dataset に落とさず fail-fast する。
 */
export function buildListSearchAnalyticsViewsDatasets(
  output: unknown,
  source: VizDataset["source"],
): VizDataset[] {
  const typedOutput = expectVisualizationObjectOutput<
    Partial<ListSaViewsOutput>
  >({
    toolName: LIST_SEARCH_ANALYTICS_VIEWS_NAME,
    output,
  });
  const rows = toSaViewRows(typedOutput);
  if (rows.length === 0) return [];
  const defaults = SA_VIEWS_DATASET_SPEC.defaults
    ? {
        ...SA_VIEWS_DATASET_SPEC.defaults,
        y: SA_VIEWS_DATASET_SPEC.defaults.y
          ? [...SA_VIEWS_DATASET_SPEC.defaults.y]
          : undefined,
      }
    : undefined;

  return [
    buildDataset({
      id: SA_VIEWS_DATASET_SPEC.id,
      profile: SA_VIEWS_DATASET_SPEC.profile,
      fields: buildFields(SA_VIEWS_DATASET_SPEC.fields),
      rows,
      defaults,
      source,
      notes: SA_VIEWS_DATASET_SPEC.notes,
    }),
  ];
}
